import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fileIcon as FileIcon } from "assets/icons";
import Sidebar from "../../components/Sidebar";
import { fetchSupportRequestById } from "../../services/supportRequestService/supportRequestService";
import { SupportRequest } from "../../services/supportRequestService/supportRequestService.types";
import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import {
  AnswerBlock,
  AnswerDate,
  AttachmentLink,
  BackLink,
  ContentWrapper,
  Label,
  Meta,
  PageState,
  QuestionBlock,
  ResponseBody,
  ResponseText,
  StatusBadge,
  SupportResponsePageContainer,
  Title,
  TitleRow,
  WaitingBlock,
} from "./SupportResponsePage.styles";

const formatUpdatedAt = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getFileNameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    return decodeURIComponent(pathname.split("/").pop() || "Файл");
  } catch {
    return decodeURIComponent(url.split("/").pop() || "Файл");
  }
};

const SupportResponsePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [response, setResponse] = useState<SupportRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useNotify();

  useEffect(() => {
    let active = true;

    const fetchResponse = async () => {
      try {
        const requestId = Number(id);
        if (!Number.isFinite(requestId)) {
          throw new Error("Некорректный идентификатор обращения");
        }

        const data = await fetchSupportRequestById(requestId);
        if (active) {
          setResponse(data);
        }
      } catch (requestError) {
        if (!active) return;

        setError("Не удалось загрузить обращение");
        logger.error(requestError);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось загрузить обращение",
          life: 3000,
        });
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void fetchResponse();

    return () => {
      active = false;
    };
  }, [id, toast]);

  const answered = Boolean(response?.response_status && response.response_content?.trim());
  const updatedAt = response ? formatUpdatedAt(response.updated_at) : "";

  return (
    <SupportResponsePageContainer>
      <Sidebar />
      <ContentWrapper>
        {loading && <PageState>Загрузка обращения...</PageState>}

        {!loading && error && <PageState>{error}</PageState>}

        {!loading && !error && response && (
          <ResponseBody>
            <BackLink type="button" onClick={() => navigate("/support-requests")}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 5l-7 7 7 7" />
              </svg>
              Все обращения
            </BackLink>

            <TitleRow>
              <Title>{response.topic}</Title>
              <StatusBadge $answered={answered}>
                {answered ? "Отвечено" : "Ждёт ответа"}
              </StatusBadge>
            </TitleRow>

            <Meta>Обновлено {updatedAt}</Meta>

            <Label>Ваш вопрос</Label>
            <QuestionBlock>
              <ResponseText>{response.question}</ResponseText>

              {response.material && (
                <AttachmentLink
                  href={response.material}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={getFileNameFromUrl(response.material)}
                >
                  <FileIcon aria-hidden="true" />
                  {getFileNameFromUrl(response.material)}
                </AttachmentLink>
              )}
            </QuestionBlock>

            <Label>Ответ</Label>
            {answered ? (
              <AnswerBlock>
                <AnswerDate>{updatedAt}</AnswerDate>
                <ResponseText>{response.response_content}</ResponseText>
              </AnswerBlock>
            ) : (
              <WaitingBlock>
                Ответа пока нет. Обычно отвечаем в течение рабочего дня, ответ появится здесь.
              </WaitingBlock>
            )}
          </ResponseBody>
        )}
      </ContentWrapper>
    </SupportResponsePageContainer>
  );
};

export default SupportResponsePage;
